const { getOffsetTop } = window.BAO.Utils

class MediaScroll extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)
    this.setupSizes = this.setupSizes.bind(this)

    this.cachedScrollPositions = null
    this.cachedMediaViewportPercent = null
    this.activeThumbIndex = -1
    this.isVisible = false
    this.lastScrollAmount = null

    this.setupSizes()

    this.setupIntersectionObserver()
    this.updateEl()
  }

  setupIntersectionObserver () {
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting
        if (!entry.isIntersecting) {
          this.isTicking = false
        } else if (entry.isIntersecting) {
          this.requestTick()
        }
      },
      { rootMargin: '200px' },
    )
    this.observer.observe(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.debouncedOnResize = window.BAO.Utils.debounce(() => {
      this.cachedScrollPositions = null
      this.cachedMediaViewportPercent = null
    }, 200)

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.debouncedOnResize)

    if (!this.els.thumbnail?.exists) return
    this.handleThumbnail = this.handleThumbnail.bind(this)

    this.els.thumbnail.elements.forEach((thumbnailEl) => {
      this.listeners.add(thumbnailEl, 'click', this.handleThumbnail)
    })
  }

  onScroll () {
    if (!this.isVisible) return

    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.moveMedia()
  }

  setupSizes () {
    const roundedHeight = Math.round(this.mediaViewportPercent)

    this.style.setProperty(
      '--Media_Total',
      `${roundedHeight > 100 ? roundedHeight : 110}vh`,
    )
  }

  moveMedia () {
    if (!window.BAO.MediaBreakpoints.isMd) return

    const mediaItemsContainer = this.els?.items?.element

    if (!mediaItemsContainer) return

    const scrollerViewportWidth =
      (mediaItemsContainer.offsetWidth / window.innerWidth) * 100

    const rawMaxScrollAmount =
      this.mediaViewportPercent - scrollerViewportWidth
    const maxScrollAmount = Math.max(0, rawMaxScrollAmount)

    if (!this.cachedScrollPositions) {
      const elementHeight = this.offsetHeight
      const offsetTop = getOffsetTop(this)
      this.cachedScrollPositions = {
        start: offsetTop,
        end: offsetTop + elementHeight - window.innerHeight,
      }
    }
    const elementStartPoint = this.cachedScrollPositions.start
    const elementEndPoint = this.cachedScrollPositions.end

    if (this.lastKnownY <= elementStartPoint) {
      this.style.setProperty('--Scroll_Amount', `0vw`)
      this.updateThumbnails(0)

      return
    }

    if (this.lastKnownY > elementEndPoint) {
      this.style.setProperty('--Scroll_Amount', `-${maxScrollAmount}vw`)
      this.updateThumbnails(1)

      return
    }

    const totalScrollAmount = elementEndPoint - elementStartPoint

    const scrollYAsAPercentageOfJourney =
      (this.lastKnownY - elementStartPoint) / totalScrollAmount

    const scrollAmount = maxScrollAmount * scrollYAsAPercentageOfJourney
    const roundedScrollAmount = Math.round(scrollAmount * 100) / 100

    // Only update if changed by at least 0.1vw
    if (
      this.lastScrollAmount !== null &&
      Math.abs(roundedScrollAmount - this.lastScrollAmount) < 0.1
    ) {
      return
    }

    this.lastScrollAmount = roundedScrollAmount
    this.style.setProperty('--Scroll_Amount', `-${roundedScrollAmount}vw`)

    this.updateThumbnails(scrollYAsAPercentageOfJourney)
  }

  updateThumbnails (scrollYAsAPercentageOfJourney) {
    if (!this.els.thumbnail?.exists) {
      return
    }

    let newActiveIndex = null

    this.els?.thumbnail?.elements.forEach((thumbEl, index) => {
      const thumbIndex = parseInt(thumbEl.dataset.index)
      const thumbMaxIndex = parseInt(thumbEl.dataset.maxIndex)

      const minimumPercent = thumbIndex / (thumbMaxIndex + 1)
      const maximumPercent = (thumbIndex + 1) / (thumbMaxIndex + 1)
      const isLastThumb = thumbIndex === thumbMaxIndex
      const isWithinRange =
        scrollYAsAPercentageOfJourney >= minimumPercent &&
        (isLastThumb
          ? scrollYAsAPercentageOfJourney <= maximumPercent
          : scrollYAsAPercentageOfJourney < maximumPercent)

      if (isWithinRange && newActiveIndex === null) {
        newActiveIndex = index
      }
    })

    if (newActiveIndex !== this.activeThumbIndex) {
      // Clear previous active thumbnail
      if (this.activeThumbIndex >= 0) {
        this.els.thumbnail.elements[this.activeThumbIndex]?.setAttribute(
          'aria-pressed',
          false,
        )
      }
      // Set new active thumbnail
      if (newActiveIndex !== null && newActiveIndex >= 0) {
        this.els.thumbnail.elements[newActiveIndex]?.setAttribute(
          'aria-pressed',
          true,
        )
        this.activeThumbIndex = newActiveIndex
      } else {
        this.activeThumbIndex = -1
      }
    }
  }

  handleThumbnail (event) {
    if (!event.target.closest(this.els?.thumbnail?.selector)) return

    const clickedTrigger = event.target.closest(this.els?.thumbnail?.selector)

    const clickedIndex = parseInt(clickedTrigger.dataset.index)
    const maxIndex = parseInt(clickedTrigger.dataset.maxIndex)

    const elementStartPoint = getOffsetTop(this)
    const elementHeight = this.offsetHeight - window.innerHeight

    const scrollCalc = (elementHeight / maxIndex) * clickedIndex
    const scrollPosition = elementStartPoint + scrollCalc

    window.scrollTo({
      top: scrollPosition - 30,
      left: 0,
      behavior: 'smooth',
    })
  }

  get mediaViewportPercent () {
    if (!this.cachedMediaViewportPercent) {
      const mediaItemsWidth = this.els.item.elements.reduce(
        (accumulator, currentValue) => {
          return accumulator + currentValue.offsetWidth
        },
        0,
      )
      this.cachedMediaViewportPercent =
        (mediaItemsWidth / window.innerWidth) * 100
    }
    return this.cachedMediaViewportPercent
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.debouncedOnResize)

    this.cachedScrollPositions = null
    this.cachedMediaViewportPercent = null
  }
}

if (!customElements.get('media-scroll')) {
  customElements.define('media-scroll', MediaScroll)
}
